import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLuxonTimezone } from 'hooks';
import { DateTime } from 'luxon';
import { AutoSizer, CellMeasurer, GridCellRenderer, MultiGrid } from 'react-virtualized';
import { UsersService } from 'services';
import { formatDateAndTimeDt } from 'services/Utils';
import { ReportsFieldType } from './enums/ReportsFieldType';
import { IRow, ReportsModel } from './ReportsModel';
import { CellFillWidthCache } from './CellFillWidthCache';

export interface IReportsTableProps {
    model: ReportsModel,
    onClickColumnHeader: (fieldId: string) => void,
    onClickRow: (row: IRow, rowIndex: number) => void,
}

export function ReportsTable({ model, onClickColumnHeader, onClickRow }: IReportsTableProps) {
    const { hasTimezone, timezone } = useLuxonTimezone(UsersService.me.timezone);
    const gridRef = useRef<MultiGrid>();

    // Use a measurer to compute the widths of the columns.
    const cacheRef = useRef<CellFillWidthCache>();
    if (!cacheRef.current) {
        cacheRef.current = new CellFillWidthCache({
            fixedHeight: true,
            keyMapper: (rowIndex, columnIndex) => {
                const hasHeaders = model.outFields.length > 0;

                if (hasHeaders) {
                    if (rowIndex === 0) {
                        return `header-${rowIndex}-${columnIndex}`;
                    } else {
                        // We need to subtract 1 from the rowIndex to account for the headers row.
                        rowIndex--;
                    }
                }

                // Our model can change at any time, so we may not have a row for the requested index.
                // This is because the cell renderer can be called asynchronously, so it may be out of
                // sync with our model.
                if (rowIndex >= model.outRows.length) {
                    // Return a separate key prefix so that we don't accidentally cache data for a real row.
                    return `indexOutOfBounds-${rowIndex}-${columnIndex}`;
                }

                return `row-${model.outRows[rowIndex].__rowId}-${columnIndex}`;
            }
        });
    }
    const cache = cacheRef.current;

    // Subscribe to model changes
    useEffect(() => {
        const calcFieldsHash = () => {
            const fieldIDs = model.outFields.map(f => f.id);
            return fieldIDs.join('_');
        }

        let lastFieldsHash = calcFieldsHash();

        return model.pubSubOnChange.subscribe(() => {
            // The model changed, so we need to tell the grid that something changed.
            gridRef.current?.forceUpdateGrids();

            // We only need to clear our width cache if the fields change.
            // This lets us keep our cache if the user only changes the sort order or filters.
            const fieldsHash = calcFieldsHash();
            if (lastFieldsHash !== fieldsHash) {
                cache.clearAll();
                lastFieldsHash = fieldsHash;
            }
        })
    }, []);

    // Since we're doing custom column widths, we need to make the grid recalculate if the window resizes.
    useEffect(() => {
        const onResize = debounce(() => {
            gridRef.current?.recomputeGridSize();
        }, 300);

        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            onResize.cancel();
        }
    }, [])

    const cellRenderer: GridCellRenderer = useCallback(({
        columnIndex,
        isScrolling,
        isVisible,
        key,
        parent,
        rowIndex,
        style,
    }) => {
        const hasHeaders = model.outFields.length > 0;
        const numHeaderRows = hasHeaders ? 1 : 0;

        // WARNING - This cell render function is called asynchronously, so our model may have already changed.
        // This means that our data like rowCount and columnCount may be stale, so we could get called
        // for rows or fields that don't exist anymore.
        if (rowIndex >= model.outRows.length + numHeaderRows) {
            return null;
        }

        if (columnIndex >= model.outFields.length) {
            return null;
        }

        const field = model.outFields[columnIndex];
        const isHeader = hasHeaders && rowIndex === 0;
        let className = '';
        let content: ReactNode = null;
        let onClick: () => void;

        if (isHeader) {
            // Lookup the current sorting status of this field.
            const sort = model.sortBy.find(s => s.fieldId === field.id);

            className = cn('reports_th', {
                reports_th_sortable: field.isSortable,
            });
            onClick = () => onClickColumnHeader(field.id);
            content = <>
                {field.label}
                {field.isSortable && sort && <>
                    {sort.ascending && <FontAwesomeIcon icon="long-arrow-alt-down" className="reports_table_sortIcon" />}
                    {!sort.ascending && <FontAwesomeIcon icon="long-arrow-alt-up" className="reports_table_sortIcon" />}
                </>}
            </>;
        } else {
            const adjustedRowIndex = rowIndex - numHeaderRows
            const row = model.outRows[adjustedRowIndex];
            const isEven = adjustedRowIndex % 2 === 0;

            className = cn('reports_td', {
                reports_rowEven: isEven,
                reports_rowOdd: !isEven,
            });
            onClick = () => onClickRow(row, rowIndex);
            content = <>
                {field.type === ReportsFieldType.String && row[field.propName]}
                {field.type === ReportsFieldType.Number && row[field.propName]}
                {field.type === ReportsFieldType.Date && (
                    formatDateAndTimeDt(row[field.propName] as DateTime, hasTimezone)
                )}
            </>;
        }
 
        // Performance optimization: CellMeasurer is slow. If this cell is already in the cache,
        // we don't need to measure it again.
        if (cache.has(rowIndex, columnIndex)) {
            return <div
                className={className}
                key={`cell-${key}`}
                onClick={onClick}
                style={style}
            >
                {content}
            </div>
        }

        return <CellMeasurer
            cache={cache}
            columnIndex={columnIndex}
            key={`cellMeasurer-${key}`}
            parent={parent}
            rowIndex={rowIndex}
        >
            {({ registerChild }) => {
                return <div
                    className={className}
                    onClick={onClick}
                    ref={registerChild}
                    style={style}
                >
                    {content}
                </div>
            }}
        </CellMeasurer>;
    }, []);

    // We're not sharing these with the cell renderer because the cell renderer is a callback that's cached,
    // so we don't want it to use stale data.
    const hasHeaders = model.outFields.length > 0;
    const numHeaderRows = hasHeaders ? 1 : 0;

    return <AutoSizer>
        {({ width, height }) => {
            cache.setContainerWidth(width);

            return <>
                <MultiGrid 
                    cellRenderer={cellRenderer}
                    classNameBottomRightGrid={cn({
                        reports_isRowClickable: model.groupBy.length > 0,
                    })}
                    columnCount={model.outFields.length}
                    columnWidth={cache.columnWidth}
                    // DON'T pass "deferredMeasurementCache". For some reason it causes MultiGrid to precompute all rows,
                    // which is super slow.
                    // deferredMeasurementCache={cache}
                    estimatedColumnSize={300}
                    estimatedRowSize={24}
                    // Fix the headers at the top.
                    fixedRowCount={1}
                    height={height}
                    ref={gridRef}
                    rowCount={numHeaderRows + model.outRows.length}
                    rowHeight={({ index }) => {
                        // Headers have more padding. We're using fixed row heights for better performance.
                        return index === 0 ? 31 : 24;
                    }}
                    width={width}
                />
                {model.outRows.length === 0 && (
                    <div className={cn('reports_noResults', {
                        'reports_noResultsHasHeaders': hasHeaders,
                    })}>
                        <span className="reports_noResultsIcon">
                            <FontAwesomeIcon icon="search" />
                        </span>
                        <span className="reports_noResultsText">
                            {model.outCustomError || `No items found.`}
                        </span>
                    </div>
                )}
            </>
        }}
    </AutoSizer>
}